/* ALOKACE PODLE TABULEK FICHTNER — cíl s termínem (metodika 2026-10-18)
 * ============================================================================
 * Majitel 18. 10. 2026 k metodice Fichtner („určitě je to super! jejich
 * metodika“): alokace podle rizikového profilu a počtu let do cíle podle
 * jejich tabulek místo naší lineární heuristiky (planSolver.alokaceProCil).
 * Zdroj: 2026-01-01-metodika-prace-s-klientem-bez-ID.pdf, s. 25 (tabulka
 * „Alokace pro cíl“), přepis docs/metodika-alokace-fichtner.md. Převzatá jsou
 * jen čísla, vizuál je náš.
 *
 * Sloupec = počet let do cíle (1 = poslední rok před cílem). Hodnoty jsou
 * procenta akcie / dluhopisy / hotovost, každý sloupec dává 100.
 *   · konzervativní 1–15 let (strop 50 % akcií, s. 24),
 *   · vyvážený 1–25 let (roky 1–13 stejné jako konzervativní),
 *   · růstový 1–18 let (u nás profil „dynamicky“).
 * Víc let do cíle, než má tabulka → poslední sloupec (tabulky se na konci
 * samy ustálí: 14 = 15, 24 = 25). Rozhodnutí (a) v docs/ZADANI-CUNG.md F9.
 *
 * Modul je BEZ ZÁVISLOSTÍ mimo lib/metodikaVolby (čte ho planSolver, který
 * se načítá i holým node).
 */
import { cistyPosun } from "./metodikaVolby";

/** Tabulky s. 25: { akcie, dluhopisy, cash } — index 0 = 1 rok do cíle. */
export const TABULKY_CIL = Object.freeze({
  konzervativni: Object.freeze({
    akcie: [0, 0, 5, 10, 15, 20, 25, 29, 34, 38, 41, 43, 48, 50, 50],
    dluhopisy: [3, 12, 20, 27, 46, 54, 60, 56, 52, 51, 50, 50, 45, 43, 43],
    cash: [97, 88, 75, 63, 39, 26, 15, 15, 14, 11, 9, 7, 7, 7, 7],
  }),
  vyvazeny: Object.freeze({
    akcie: [0, 0, 5, 10, 15, 20, 25, 29, 34, 38, 41, 43, 48, 53, 57, 62, 64, 66, 75, 85, 90, 94, 99, 100, 100],
    dluhopisy: [3, 12, 20, 27, 46, 54, 60, 56, 52, 51, 50, 50, 45, 40, 37, 35, 33, 31, 22, 13, 8, 5, 0, 0, 0],
    cash: [97, 88, 75, 63, 39, 26, 15, 15, 14, 11, 9, 7, 7, 7, 6, 3, 3, 3, 3, 2, 2, 1, 1, 0, 0],
  }),
  rustovy: Object.freeze({
    akcie: [0, 3, 23, 43, 51, 58, 65, 72, 78, 85, 92, 93, 93, 94, 95, 96, 98, 100],
    dluhopisy: [29, 38, 37, 20, 38, 38, 31, 25, 20, 15, 8, 7, 7, 6, 5, 4, 2, 0],
    cash: [71, 59, 40, 37, 11, 4, 4, 3, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  }),
});

/** Náš klíč profilu → klíč tabulky. „dynamicky“ = jejich růstový
 * (rozhodnutí (b) F9). Neznámý / prázdný profil → vyvážený (jako PROFILY v solveru). */
export function profilTabulky(profil) {
  if (profil === "dynamicky" || profil === "rustovy") return "rustovy";
  if (profil === "konzervativni") return "konzervativni";
  return "vyvazeny";
}

/**
 * Alokace pro cíl za `roky` let podle tabulky profilu.
 * `roky` se zaokrouhlí; 0, záporné, NaN, prázdné → 1 (poslední rok před cílem,
 * nejopatrnější sloupec); víc let než tabulka (i Infinity) → poslední sloupec.
 * @param {number} roky počet let do cíle (včetně letošního)
 * @param {string} profil "konzervativni" | "vyvazeny" | "dynamicky"
 * @returns {{akcie: number, dluhopisy: number, cash: number}} procenta, součet 100
 */
export function alokaceZTabulky(roky, profil = "vyvazeny") {
  const t = TABULKY_CIL[profilTabulky(profil)];
  const delka = t.akcie.length;
  const n = +roky;
  const sloupec = n === Infinity ? delka : Math.max(1, Math.min(delka, Math.round(Number.isFinite(n) ? n : 1)));
  const i = sloupec - 1;
  return { akcie: t.akcie[i], dluhopisy: t.dluhopisy[i], cash: t.cash[i] };
}

/**
 * „DYNAMIČTĚJŠÍ“ ALOKACE (Nastavení → Metodika plánu, majitel 19. 10. 2026:
 * „víc dynamicky, ať to není konzervativní jenom“): sloupec tabulky o `posun`
 * let dál, tedy rozložení, jaké by cíl měl o N let dřív před termínem — víc
 * akcií dřív, i v posledním roce. Posun 0–5 (`metodikaVolby.cistyPosun`:
 * NaN / záporný / text → 0, nad 5 → 5); 0 = `alokaceZTabulky` beze změny.
 * Konec tabulky drží poslední sloupec jako `alokaceZTabulky`.
 * @param {number} roky počet let do cíle
 * @param {string} profil
 * @param {number} posun o kolik let posunout sloupec (0–5)
 */
export function alokaceZTabulkyPosun(roky, profil = "vyvazeny", posun = 0) {
  const p = cistyPosun(posun);
  const n = +roky;
  if (p === 0 || n === Infinity) return alokaceZTabulky(roky, profil);
  const sloupec = Math.max(1, Math.round(Number.isFinite(n) ? n : 1));
  return alokaceZTabulky(sloupec + p, profil);
}
